import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/program_pro_empty.svg"
import { useBoxExploreSearchContents } from "@modules/boxB2BProgram/hooks/useBoxExplore"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { EmptyView } from "@modules/ui/components/emptyView"
import { Text } from "@/components/ui/baseUi/text"
import { useCallback, useEffect, useRef } from "react"

type BoxExploreB2BProgramsListProps = { searchTerms?: string; className?: string } & React.ComponentProps<"div">

export function BoxExploreB2BProgramsList({ searchTerms, className, ...props }: BoxExploreB2BProgramsListProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const {
    data: { content: exploreContent },
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBoxExploreSearchContents(searchTerms ?? "")
  const { navigateToProgramView } = useProgramSidebar()

  const onViewProgramClick = (programId: string) => {
    navigateToProgramView(programId, false, { replaceAllComponents: false })
  }

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {exploreContent.length === 0 && (
        <div className="flex h-full flex-col items-center justify-center">
          <EmptyView
            image={{ src: SearchEmpty }}
            title={word("explore.search.no_result.title", { searchTerms })}
            description={word("explore.search.no_result.message")}
            flexGrow={false}
            className="p-12"
            textAlign="left"
            disposition="row-reverse"
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          />
        </div>
      )}

      <div className="flex flex-wrap justify-items-center gap-4">
        {exploreContent.map((itemContent, index) => (
          <ExploreCard
            key={index}
            item={itemContent.program}
            onViewDetailsClick={() => onViewProgramClick(itemContent.program.id)}
            isSelected={false}
            size="h-53 w-62"
          />
        ))}
      </div>
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
          <Text fontStyle="BM">{word("global.loading")}</Text>
        </div>
      )}
    </div>
  )
}

export function BoxExploreB2BProgramsListSkeleton() {
  return (
    <div className="flex flex-row flex-wrap justify-between gap-4 pt-6">
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
      <Skeleton className="h-53 w-62" />
    </div>
  )
}
